"use client";

import Image from "next/image";
import Link from "next/link";
import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  clearCadastroProfile,
  loadCadastroProfile,
  saveCadastroProfile,
  type CadastroProfile,
} from "@/services/cadastroSession";
import {
  formatBirthDateForDisplay,
  formatBirthDateInput,
  parseBirthDate,
} from "@/utils/birthDate";
import RankingProfile from "../rankingProfile";

interface ProfilePageContentProps {
  initialEmail: string | null;
}

interface ProfileFormState {
  nome: string;
  sobrenome: string;
  celular: string;
  email: string;
  dataNascimento: string;
}

function formatPhone(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 2) return digits ? `(${digits}` : "";
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function toFormState(profile: CadastroProfile): ProfileFormState {
  return {
    nome: profile.nome,
    sobrenome: profile.sobrenome,
    celular: formatPhone(profile.celular),
    email: profile.email,
    dataNascimento: formatBirthDateForDisplay(profile.dataNascimento),
  };
}

function isUnauthorizedStatus(status: number) {
  return status === 401 || status === 403;
}

function activateEditing(setEditing: (value: boolean) => void) {
  return () => setEditing(true);
}

export function ProfilePageContent({ initialEmail }: ProfilePageContentProps) {
  const [profile, setProfile] = useState<CadastroProfile | null>(null);
  const [form, setForm] = useState<ProfileFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const startEditing = activateEditing(setEditing);

  useEffect(() => {
    const cachedProfile = loadCadastroProfile();

    if (cachedProfile) {
      setProfile(cachedProfile);
      setForm(toFormState(cachedProfile));
    }

    async function loadProfile() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch("/api/cadastro/me", {
          credentials: "include",
        });

        const data = await response.json();

        if (!response.ok) {
          if (isUnauthorizedStatus(response.status)) {
            clearCadastroProfile();
            setProfile(null);
            setForm(null);
            setSessionExpired(true);
            return;
          }

          throw new Error(
            data.error ?? "Não foi possível carregar seu perfil.",
          );
        }

        saveCadastroProfile(data.user);
        setProfile(data.user);
        setForm(toFormState(data.user));
        setSessionExpired(false);
      } catch (loadError) {
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Não foi possível carregar seu perfil.",
        );
      } finally {
        setLoading(false);
      }
    }

    void loadProfile();
  }, []);

  const isFormReady = useMemo(() => {
    if (!form) return false;

    return (
      form.nome.trim() &&
      form.sobrenome.trim() &&
      form.celular.replace(/\D/g, "").length === 11 &&
      Boolean(parseBirthDate(form.dataNascimento))
    );
  }, [form]);

  function handleFieldChange(field: keyof ProfileFormState) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const value =
        field === "celular"
          ? formatPhone(event.target.value)
          : field === "dataNascimento"
            ? formatBirthDateInput(event.target.value)
          : event.target.value;

      setForm((current) =>
        current ? { ...current, [field]: value } : current,
      );
    };
  }

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!form) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch("/api/cadastro/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          nome: form.nome,
          sobrenome: form.sobrenome,
          celular: form.celular,
          dataNascimento: form.dataNascimento,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (isUnauthorizedStatus(response.status)) {
          clearCadastroProfile();
          setProfile(null);
          setForm(null);
          setSessionExpired(true);
          throw new Error(
            "Sua sessão expirou. Entre novamente para continuar.",
          );
        }

        throw new Error(data.error ?? "Não foi possível atualizar seu perfil.");
      }

      saveCadastroProfile(data.user);
      setProfile(data.user);
      setForm(toFormState(data.user));
      setEditing(false);
      setSuccessMessage("Perfil atualizado com sucesso.");
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Não foi possível atualizar seu perfil.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteProfile() {
    setDeleting(true);
    setError(null);

    try {
      const response = await fetch("/api/cadastro/me", {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        if (isUnauthorizedStatus(response.status)) {
          clearCadastroProfile();
          setProfile(null);
          setForm(null);
          setSessionExpired(true);
        }

        throw new Error(data.error ?? "Não foi possível excluir seu perfil.");
      }

      clearCadastroProfile();
      window.location.href = "/cadastro";
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Não foi possível excluir seu perfil.",
      );
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  }

  if (loading && !form) {
    return (
      <div className="flex min-h-[420px] w-full items-center justify-center bg-white px-6 py-16 text-center text-[#2033ef]">
        <p className="text-[18px] font-extrabold uppercase">
          Carregando perfil...
        </p>
      </div>
    );
  }

  if (sessionExpired && !form) {
    return (
      <div className="flex w-full justify-center bg-white px-5 py-12">
        <div className="w-full max-w-[420px] rounded-[18px] bg-[#f4f4f4] px-6 py-8 text-center text-black">
          <h1 className="text-[24px] font-extrabold uppercase text-[#2033ef]">
            Seu perfil na Casa CazéTV
          </h1>
          <p className="mt-5 text-[15px] leading-[1.25]">
            Sua sessão expirou. Entre novamente para editar ou excluir seu
            perfil.
          </p>
          <p className="mt-2 text-[14px] text-black/65">{initialEmail}</p>
          <Link
            href="/entrar"
            className="mt-7 inline-flex rounded-full bg-[#2033ef] px-6 py-3 text-[14px] font-extrabold uppercase text-white"
          >
            Fazer login
          </Link>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex w-full justify-center bg-white px-5 py-12">
        <div className="w-full max-w-[420px] rounded-[18px] bg-[#f4f4f4] px-6 py-8 text-center text-black">
          <h1 className="text-[24px] font-extrabold uppercase text-[#2033ef]">
            Seu perfil na Casa CazéTV
          </h1>
          <p className="mt-5 text-[15px] leading-[1.25]">
            {error ?? "Não foi possível carregar seu perfil agora."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full justify-center bg-white px-5 py-8">
        <form onSubmit={handleSave} className="w-full max-w-[420px]">
          <h1 className="text-center text-[24px] font-extrabold uppercase text-[#2033ef]">
            Seu perfil na Casa CazéTV
          </h1>

          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="sr-only">Nome</span>
              <input
                value={form.nome}
                onChange={handleFieldChange("nome")}
                onFocus={startEditing}
                readOnly={!editing}
                disabled={saving}
                placeholder="Nome"
                className="h-[48px] w-full rounded-[4px] bg-[#e5e5e5] px-4 text-[15px] text-black outline-none read-only:cursor-text disabled:text-black/70"
              />
            </label>
            <label className="block">
              <span className="sr-only">Sobrenome</span>
              <input
                value={form.sobrenome}
                onChange={handleFieldChange("sobrenome")}
                onFocus={startEditing}
                readOnly={!editing}
                disabled={saving}
                placeholder="Sobrenome"
                className="h-[48px] w-full rounded-[4px] bg-[#e5e5e5] px-4 text-[15px] text-black outline-none read-only:cursor-text disabled:text-black/70"
              />
            </label>
            <label className="block">
              <span className="sr-only">Celular</span>
              <input
                value={form.celular}
                onChange={handleFieldChange("celular")}
                onFocus={startEditing}
                readOnly={!editing}
                disabled={saving}
                placeholder="Celular"
                inputMode="numeric"
                maxLength={15}
                className="h-[48px] w-full rounded-[4px] bg-[#e5e5e5] px-4 text-[15px] text-black outline-none read-only:cursor-text disabled:text-black/70"
              />
            </label>
            <label className="block">
              <span className="sr-only">Data de nascimento</span>
              <input
                value={form.dataNascimento}
                onChange={handleFieldChange("dataNascimento")}
                onFocus={startEditing}
                readOnly={!editing}
                disabled={saving}
                type="text"
                inputMode="numeric"
                maxLength={10}
                placeholder="Data de nascimento"
                className="h-[48px] w-full rounded-[4px] bg-[#e5e5e5] px-4 text-[15px] text-black outline-none read-only:cursor-text disabled:text-black/70"
              />
            </label>
            <label className="block">
              <span className="sr-only">E-mail</span>
              <input
                value={form.email}
                disabled
                placeholder="E-mail"
                className="h-[48px] w-full rounded-[4px] bg-[#e5e5e5] px-4 text-[15px] text-black/65 outline-none"
              />
            </label>
          </div>

          {!editing ? (
            <p className="mt-3 text-center text-[12px] text-black/55">
              Toque em um campo ou no botão editar para alterar seus dados.
            </p>
          ) : null}

          {error ? (
            <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] leading-snug text-red-600">
              {error}
            </p>
          ) : null}

          {successMessage ? (
            <p className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] leading-snug text-green-700">
              {successMessage}
            </p>
          ) : null}

          {!editing ? (
            <>
              <div className="mt-8 flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(true);
                    setSuccessMessage(null);
                  }}
                  className="block flex-1"
                  aria-label="Editar perfil"
                >
                  <Image
                    src="/assets/profile/edit.svg"
                    alt="Editar"
                    width={418}
                    height={119}
                    className="h-auto w-full"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="block flex-1"
                  aria-label="Excluir acesso"
                >
                  <Image
                    src="/assets/profile/excluir.svg"
                    alt="Excluir acesso"
                    width={418}
                    height={119}
                    className="h-auto w-full"
                  />
                </button>
              </div>

              <div className="text-black">
                <RankingProfile />
              </div>
            </>
          ) : (
            <div className="mt-8 flex items-center gap-4">
              <button
                type="submit"
                disabled={saving || !isFormReady}
                className="h-[56px] flex-1 cursor-pointer rounded-[4px] bg-[#fec822] px-5 text-[18px] font-extrabold uppercase text-[#2033ef] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "Salvando..." : "Salvar"}
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  if (!profile) return;
                  setForm(toFormState(profile));
                  setEditing(false);
                  setError(null);
                  setSuccessMessage(null);
                }}
                className="h-[56px] flex-1 cursor-pointer rounded-[4px] bg-[#2033ef] px-5 text-[18px] font-extrabold uppercase text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          )}
        </form>
      </div>

      {showDeleteModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6">
          <div className="relative w-full max-w-[320px] rounded-[18px] bg-[#fec822] px-8 pb-8 pt-11 text-center text-black">
            <button
              type="button"
              onClick={() => setShowDeleteModal(false)}
              className="absolute -right-[10px] -top-[14px] flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-[#2f43ef] text-white"
              aria-label="Fechar confirmação"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.7"
                className="h-5 w-5"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>

            <h2 className="text-[24px] font-extrabold uppercase leading-none text-[#2033ef]">
              Tem certeza?
            </h2>

            <p className="mt-6 text-[15px] leading-[1.15]">
              Ao excluir seu perfil, você perderá seu histórico de jogos,
              posição no ranking e lugar nas filas virtuais.
            </p>

            <p className="mt-5 text-[15px] leading-[1.15]">
              Você poderá se cadastrar novamente depois, mas os dados anteriores
              não poderão ser recuperados.
            </p>

            <label className="mt-6 flex items-start gap-3 text-left">
              <input
                type="radio"
                checked
                readOnly
                className="mt-1 h-[14px] w-[14px] shrink-0 appearance-none rounded-full border border-black bg-white p-[2px] checked:bg-[radial-gradient(circle,#000_0_45%,transparent_47%)]"
              />
              <span className="text-[14px] font-semibold leading-[1.2]">
                Confirmo que desejo excluir meu perfil e estou ciente de que
                meus dados não poderão ser recuperados.
              </span>
            </label>

            <button
              type="button"
              onClick={handleDeleteProfile}
              disabled={deleting}
              className="mt-7 block w-full"
              aria-label="Excluir perfil"
            >
              <Image
                src="/assets/profile/excluir-perfil.svg"
                alt="Excluir perfil"
                width={583}
                height={119}
                className={`h-auto w-full ${deleting ? "opacity-60" : ""}`}
              />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
